import { buildCacheKeyFromAxiosConfig, cacheGetResponse, getCachedResponse } from './httpCache';
import { getApiAccessToken } from '../config/authToken';
import { queueOpFromAxiosConfig, flushPendingOps } from './pendingSync';
import { buildOfflineQueuedResponseData, ensurePostPayloadId } from './mutationPayload';

function toUrlString(input) {
  if (typeof input === 'string') return input;
  if (input && typeof input.url === 'string') return input.url;
  return String(input);
}

function isApiUrl(url) {
  try {
    // Same-origin / relative
    if (url.startsWith('/api/')) return true;
    // Absolute to same host
    const u = new URL(url, window.location.origin);
    return u.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

function normalizeRelativeApiUrl(url) {
  if (url.startsWith('/api/')) return url;
  try {
    const u = new URL(url, window.location.origin);
    return `${u.pathname}${u.search}`;
  } catch {
    return url;
  }
}

function makeCacheKey(method, url) {
  // Reuse axios-style key builder by shaping a config
  return buildCacheKeyFromAxiosConfig({
    method,
    baseURL: '',
    url,
  });
}

export function patchFetchForOffline() {
  if (typeof window === 'undefined') return;
  if (window.__madFetchPatched) return;
  window.__madFetchPatched = true;

  const originalFetch = window.fetch.bind(window);
  window.__madOriginalFetch = originalFetch;

  window.addEventListener('online', () => {
    flushPendingOps().catch(() => {});
  });

  window.fetch = async (input, init = {}) => {
    const urlRaw = toUrlString(input);
    if (!isApiUrl(urlRaw)) {
      return originalFetch(input, init);
    }

    const method = String(init.method || 'GET').toUpperCase();
    const url = normalizeRelativeApiUrl(urlRaw);

    // Always send cookies for our API + Bearer, если cookie нет (Mini App / кросс-домен)
    const bearer = getApiAccessToken();
    const headers = { ...(init.headers || {}) };
    if (bearer && !headers.Authorization) {
      headers.Authorization = `Bearer ${bearer}`;
    }
    const nextInit = {
      ...init,
      credentials: init.credentials || 'include',
      headers,
    };

    // GET: try network, fallback to cache
    if (method === 'GET') {
      const cacheKey = makeCacheKey('get', url);
      try {
        const resp = await originalFetch(url, nextInit);
        if (resp && resp.ok) {
          const contentType = resp.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const cloned = resp.clone();
            cloned
              .json()
              .then((data) => cacheGetResponse(cacheKey, data))
              .catch(() => {});
          }
        }
        return resp;
      } catch (e) {
        const cached = await getCachedResponse(cacheKey);
        if (cached !== null && cached !== undefined) {
          return new Response(JSON.stringify(cached), {
            status: 200,
            headers: { 'Content-Type': 'application/json' },
          });
        }
        throw e;
      }
    }

    // Mutations: if offline/network fails, queue
    try {
      const resp = await originalFetch(url, nextInit);
      return resp;
    } catch (e) {
      // Do NOT queue auth operations; without network they must fail fast (no «успех» с 202 в очереди).
      const skipQueue =
        url.startsWith('/api/users/login') ||
        url.startsWith('/api/users/logout') ||
        (url === '/api/users' && method === 'POST') ||
        url.startsWith('/api/auth/forgot-password') ||
        url.startsWith('/api/auth/reset-password') ||
        url.startsWith('/api/auth/google') ||
        url.startsWith('/api/auth/apple') ||
        url.startsWith('/api/auth/telegram');
      if (skipQueue) {
        throw e;
      }

      // Try to parse body as JSON if possible for replay
      let body = null;
      try {
        if (nextInit.body && typeof nextInit.body === 'string') {
          body = JSON.parse(nextInit.body);
        } else if (nextInit.body && typeof nextInit.body === 'object') {
          body = nextInit.body;
        }
      } catch {
        body = nextInit.body || null;
      }

      const axiosLike = {
        method,
        url,
        data: body,
      };
      ensurePostPayloadId(axiosLike);

      await queueOpFromAxiosConfig({
        method,
        baseURL: '',
        url,
        headers: nextInit.headers || {},
        data: axiosLike.data,
      });

      const payload = buildOfflineQueuedResponseData(axiosLike);
      try {
        window.dispatchEvent(new CustomEvent('mad:offlineMutationQueued'));
      } catch {
        /* ignore */
      }
      return new Response(JSON.stringify(payload), {
        status: 202,
        headers: { 'Content-Type': 'application/json' },
      });
    }
  };
}

